import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * <RelatedLinks /> — Shared internal-link cluster.
 *
 * Usage:
 *   <RelatedLinks
 *     title="Continue exploring"
 *     items={[{label: "Bourbon", to: "/spirits/category/bourbon"}, ...]}
 *   />
 */
export const RelatedLinks = ({
    title = "Continue exploring",
    eyebrow = "Related",
    items = [],
    testid = "related-links",
    columns = 3,
}) => {
    if (!items.length) return null;
    return (
        <section
            data-testid={testid}
            style={{
                background: "var(--ls-panel)",
                border: "1px solid var(--ls-border)",
                borderRadius: 8,
                padding: 32,
                marginTop: 48,
            }}
        >
            <span className="eyebrow">{eyebrow}</span>
            <h3
                className="font-display text-white mt-3"
                style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.2, letterSpacing: "-0.015em" }}
            >
                {title}
            </h3>
            <ul
                style={{
                    marginTop: 22,
                    display: "grid",
                    gridTemplateColumns: `repeat(auto-fit, minmax(${columns === 2 ? 260 : 200}px, 1fr))`,
                    gap: 14,
                    listStyle: "none",
                    padding: 0,
                }}
            >
                {items.map((it, i) => {
                    const isExternal = it.external || (it.to && /^https?:\/\//.test(it.to));
                    const inner = (
                        <span className="flex items-center justify-between gap-3" style={{ width: "100%" }}>
                            <span>
                                <span className="text-white" style={{ fontSize: 14, fontWeight: 500, display: "block" }}>{it.label}</span>
                                {it.hint && (
                                    <span className="block mt-0.5 text-xs" style={{ color: "var(--ls-text-muted)" }}>{it.hint}</span>
                                )}
                            </span>
                            <ArrowRight size={14} style={{ color: "var(--ls-gold)", flexShrink: 0 }} />
                        </span>
                    );
                    const linkStyle = {
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "14px 16px",
                        borderRadius: 6,
                        border: "1px solid var(--ls-border)",
                        background: "var(--ls-bg)",
                        transition: "border-color 0.2s ease, transform 0.2s ease",
                        textDecoration: "none",
                    };
                    return (
                        <li key={i}>
                            {isExternal ? (
                                <a
                                    href={it.to}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={linkStyle}
                                    data-testid={`${testid}-item-${i}`}
                                    className="related-link-row"
                                >
                                    {inner}
                                </a>
                            ) : (
                                <Link
                                    to={it.to}
                                    style={linkStyle}
                                    data-testid={`${testid}-item-${i}`}
                                    className="related-link-row"
                                >
                                    {inner}
                                </Link>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default RelatedLinks;
